import { IPeriodScores } from "@finbackoffice/clientbff-client";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";

export const useLiveScoreboard = (scores: IPeriodScores[] = [], slidesToShow: number) => {
    const quartersRef = useRef<HTMLDivElement>(null);
    const touchStart = useRef(0);
    const isTouching = useRef(false);

    const scoresLength = scores?.length || 0;

    const defaultCarouselCurrentIndex = useMemo(
        () => (scoresLength > slidesToShow ? scoresLength - slidesToShow : 0),
        [slidesToShow, scoresLength],
    );

    const [carouselCurrentIndex, setCarouselCurrentIndex] = useState(defaultCarouselCurrentIndex);

    useEffect(() => {
        setCarouselCurrentIndex(defaultCarouselCurrentIndex);
    }, [defaultCarouselCurrentIndex, slidesToShow, scoresLength]);

    const onTouchStart = useCallback((event: TouchEvent) => {
        isTouching.current = true;
        touchStart.current = event.touches[0].pageX;
    }, []);

    const onTouchMove = useCallback(
        (event: TouchEvent) => {
            if (isTouching.current) {
                if (touchStart.current - event.touches[0].pageX > 40) {
                    if (carouselCurrentIndex < scoresLength - slidesToShow) {
                        setCarouselCurrentIndex(carouselCurrentIndex + 1);
                        isTouching.current = false;
                    }
                } else if (touchStart.current - event.touches[0].pageX < -40) {
                    if (carouselCurrentIndex > 0) {
                        setCarouselCurrentIndex(carouselCurrentIndex - 1);
                        isTouching.current = false;
                    }
                }
            }
        },
        [carouselCurrentIndex, scoresLength, slidesToShow],
    );

    useEffect(() => {
        if (!quartersRef.current) {
            return undefined;
        }

        const quartersRefCurrent = quartersRef.current;
        quartersRefCurrent.addEventListener("touchstart", onTouchStart);
        quartersRefCurrent.addEventListener("touchmove", onTouchMove);

        return () => {
            if (quartersRefCurrent) {
                quartersRefCurrent.removeEventListener("touchstart", onTouchStart);
                quartersRefCurrent.removeEventListener("touchmove", onTouchMove);
            }
        };
    }, [onTouchMove, onTouchStart]);

    const isHomeWinning = useMemo(
        () => (period: IPeriodScores) => period.home_score > period.away_score,
        [],
    );
    const isAwayWinning = useMemo(
        () => (period: IPeriodScores) => period.away_score > period.home_score,
        [],
    );

    const isCurrentScore = useMemo(
        () => (index: number) => index === scoresLength - 1,
        [scoresLength],
    );

    const onPrevClick = useCallback(() => {
        setCarouselCurrentIndex((state) => state - 1);
    }, []);

    const onNextClick = useCallback(() => {
        setCarouselCurrentIndex((state) => state + 1);
    }, []);

    const carouselEnabled = useMemo(
        () => scoresLength - slidesToShow > 0,
        [slidesToShow, scoresLength],
    );

    return {
        carouselCurrentIndex,
        onPrevClick,
        onNextClick,
        scores,
        isHomeWinning,
        isAwayWinning,
        isCurrentScore,
        quartersRef,
        carouselEnabled,
        nextBtnDisabled: carouselCurrentIndex === scoresLength - slidesToShow,
        prevBtnDisabled: carouselCurrentIndex === 0,
    };
};
